import { MMKV } from 'react-native-mmkv';
import { Platform } from 'react-native';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const logger = createScopedLogger('UserProfileCache');
const logWithTimestamp = (msg: string, ...args: unknown[]) => logger.info(msg, ...args);
const errorWithTimestamp = (msg: string, ...args: unknown[]) => logger.error(msg, ...args);

const CACHE_KEY_PREFIX = 'user_profile_cache_';
const GAMIFICATION_KEY_PREFIX = 'gamification_cache_';
const NOTIFICATIONS_KEY_PREFIX = 'notification_prefs_cache_';
const LAST_REFRESH_KEY = 'user_cache_last_refresh';

const PROFILE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const NOTIFICATIONS_TTL = 6 * 60 * 60 * 1000; // 6 hours
const GAMIFICATION_TTL = 1 * 60 * 60 * 1000; // 1 hour

const isWeb = Platform.OS === 'web';

let mmkvInstance: MMKV | null = null;

const getMMKV = (): MMKV => {
  if (!mmkvInstance) {
    mmkvInstance = new MMKV({
      id: 'user-profile-cache',
    });
  }
  return mmkvInstance;
};

export interface CachedUserProfile {
  smartuserId: string;
  email: string | null;
  phone: string | null;
  pseudo: string | null;
  birthday: string | null;
  profilePictureUrl: string | null;
  isPaying: boolean;
  createdAt: string | null;
  timestamp: number;
}

export interface CachedGamification {
  smartuserId: string;
  totalCoins: number;
  consecutiveDaysStreak: number;
  lastVisitDate: string | null;
  timestamp: number;
}

export interface CachedNotificationPreferences {
  smartuserId: string | null;
  newEpisodes: boolean;
  recommendations: boolean;
  subscriptionReminders: boolean;
  rewards: boolean;
  marketing: boolean;
  timestamp: number;
}

interface CacheStorage {
  getString(key: string): string | undefined;
  setString(key: string, value: string): void;
  delete(key: string): void;
  contains(key: string): boolean;
  getAllKeys(): string[];
}

const createWebStorage = (): CacheStorage => ({
  getString: (key: string): string | undefined => {
    try {
      const value = localStorage.getItem(key);
      return value ?? undefined;
    } catch {
      return undefined;
    }
  },
  setString: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.error('[USER-PROFILE-CACHE] Web localStorage write error:', e);
    }
  },
  delete: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch {}
  },
  contains: (key: string): boolean => {
    try {
      return localStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  },
  getAllKeys: (): string[] => {
    try {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) keys.push(key);
      }
      return keys;
    } catch {
      return [];
    }
  },
});

const createNativeStorage = (): CacheStorage => {
  const mmkv = getMMKV();
  return {
    getString: (key: string): string | undefined => mmkv.getString(key),
    setString: (key: string, value: string): void => mmkv.set(key, value),
    delete: (key: string): void => mmkv.delete(key),
    contains: (key: string): boolean => mmkv.contains(key),
    getAllKeys: (): string[] => mmkv.getAllKeys(),
  };
};

const storage: CacheStorage = isWeb ? createWebStorage() : createNativeStorage();

class UserProfileCacheService {
  private initialized = false;

  initialize(): void {
    if (this.initialized) return;
    logWithTimestamp('👤 [USER-PROFILE-CACHE] Initializing user profile cache service');
    this.initialized = true;
  }

  private isExpired(timestamp: number, ttl: number): boolean {
    return Date.now() - timestamp > ttl;
  }

  setUserProfile(profile: CachedUserProfile): void {
    this.initialize();
    const key = `${CACHE_KEY_PREFIX}${profile.smartuserId}`;
    try {
      storage.setString(key, JSON.stringify(profile));
      logWithTimestamp(`👤 [USER-PROFILE-CACHE] Profile cached for user ${profile.smartuserId}`);
    } catch (error) {
      errorWithTimestamp('👤 [USER-PROFILE-CACHE] Error caching profile:', error);
    }
  }

  getUserProfile(smartuserId: string): CachedUserProfile | null {
    this.initialize();
    const key = `${CACHE_KEY_PREFIX}${smartuserId}`;
    try {
      const cached = storage.getString(key);
      if (!cached) {
        logWithTimestamp(`👤 [USER-PROFILE-CACHE] Profile cache MISS for ${smartuserId}`);
        return null;
      }

      const profile: CachedUserProfile = JSON.parse(cached);
      if (this.isExpired(profile.timestamp, PROFILE_TTL)) {
        logWithTimestamp(`👤 [USER-PROFILE-CACHE] Profile cache EXPIRED for ${smartuserId}`);
        storage.delete(key);
        return null;
      }

      logWithTimestamp(`👤 [USER-PROFILE-CACHE] Profile cache HIT for ${smartuserId}`);
      return profile;
    } catch (error) {
      errorWithTimestamp('👤 [USER-PROFILE-CACHE] Error reading profile cache:', error);
      return null;
    }
  }

  isProfileCacheValid(smartuserId: string): boolean {
    const profile = this.getUserProfile(smartuserId);
    return profile !== null;
  }

  setGamification(gamification: CachedGamification): void {
    this.initialize();
    const key = `${GAMIFICATION_KEY_PREFIX}${gamification.smartuserId}`;
    try {
      storage.setString(key, JSON.stringify(gamification));
      logWithTimestamp(`🎮 [USER-PROFILE-CACHE] Gamification cached for user ${gamification.smartuserId}: ${gamification.totalCoins} coins`);
    } catch (error) {
      errorWithTimestamp('🎮 [USER-PROFILE-CACHE] Error caching gamification:', error);
    }
  }

  getGamification(smartuserId: string): CachedGamification | null {
    this.initialize();
    const key = `${GAMIFICATION_KEY_PREFIX}${smartuserId}`;
    try {
      const cached = storage.getString(key);
      if (!cached) {
        logWithTimestamp(`🎮 [USER-PROFILE-CACHE] Gamification cache MISS for ${smartuserId}`);
        return null;
      }

      const gamification: CachedGamification = JSON.parse(cached);
      if (this.isExpired(gamification.timestamp, GAMIFICATION_TTL)) {
        logWithTimestamp(`🎮 [USER-PROFILE-CACHE] Gamification cache EXPIRED for ${smartuserId}`);
        storage.delete(key);
        return null;
      }

      logWithTimestamp(`🎮 [USER-PROFILE-CACHE] Gamification cache HIT for ${smartuserId}: ${gamification.totalCoins} coins`);
      return gamification;
    } catch (error) {
      errorWithTimestamp('🎮 [USER-PROFILE-CACHE] Error reading gamification cache:', error);
      return null;
    }
  }

  invalidateGamification(smartuserId: string): void {
    this.initialize();
    const key = `${GAMIFICATION_KEY_PREFIX}${smartuserId}`;
    try {
      storage.delete(key);
      logWithTimestamp(`🎮 [USER-PROFILE-CACHE] Gamification cache INVALIDATED for ${smartuserId}`);
    } catch (error) {
      errorWithTimestamp('🎮 [USER-PROFILE-CACHE] Error invalidating gamification cache:', error);
    }
  }

  setNotificationPreferences(prefs: CachedNotificationPreferences): void {
    this.initialize();
    const key = `${NOTIFICATIONS_KEY_PREFIX}${prefs.smartuserId || 'anonymous'}`;
    try {
      storage.setString(key, JSON.stringify(prefs));
      logWithTimestamp(`🔔 [USER-PROFILE-CACHE] Notification preferences cached for ${prefs.smartuserId || 'anonymous'}`);
    } catch (error) {
      errorWithTimestamp('🔔 [USER-PROFILE-CACHE] Error caching notification preferences:', error);
    }
  }

  getNotificationPreferences(smartuserId: string | null): CachedNotificationPreferences | null {
    this.initialize();
    const key = `${NOTIFICATIONS_KEY_PREFIX}${smartuserId || 'anonymous'}`;
    try {
      const cached = storage.getString(key);
      if (!cached) {
        logWithTimestamp(`🔔 [USER-PROFILE-CACHE] Notification preferences cache MISS for ${smartuserId || 'anonymous'}`);
        return null;
      }

      const prefs: CachedNotificationPreferences = JSON.parse(cached);
      if (this.isExpired(prefs.timestamp, NOTIFICATIONS_TTL)) {
        logWithTimestamp(`🔔 [USER-PROFILE-CACHE] Notification preferences cache EXPIRED for ${smartuserId || 'anonymous'}`);
        storage.delete(key);
        return null;
      }

      logWithTimestamp(`🔔 [USER-PROFILE-CACHE] Notification preferences cache HIT for ${smartuserId || 'anonymous'}`);
      return prefs;
    } catch (error) {
      errorWithTimestamp('🔔 [USER-PROFILE-CACHE] Error reading notification preferences cache:', error);
      return null;
    }
  }

  setLastRefreshTimestamp(): void {
    this.initialize();
    try {
      storage.setString(LAST_REFRESH_KEY, Date.now().toString());
      logWithTimestamp('👤 [USER-PROFILE-CACHE] Last refresh timestamp updated');
    } catch (error) {
      errorWithTimestamp('👤 [USER-PROFILE-CACHE] Error setting last refresh timestamp:', error);
    }
  }

  getLastRefreshTimestamp(): number {
    this.initialize();
    try {
      const timestamp = storage.getString(LAST_REFRESH_KEY);
      return timestamp ? parseInt(timestamp, 10) : 0;
    } catch {
      return 0;
    }
  }

  needsPeriodicRefresh(intervalMs: number = 6 * 60 * 60 * 1000): boolean {
    const lastRefresh = this.getLastRefreshTimestamp();
    if (lastRefresh === 0) return true;
    return Date.now() - lastRefresh > intervalMs;
  }

  clearUserData(smartuserId: string): void {
    this.initialize();
    try {
      const profileKey = `${CACHE_KEY_PREFIX}${smartuserId}`;
      const gamificationKey = `${GAMIFICATION_KEY_PREFIX}${smartuserId}`;
      const notificationsKey = `${NOTIFICATIONS_KEY_PREFIX}${smartuserId}`;

      storage.delete(profileKey);
      storage.delete(gamificationKey);
      storage.delete(notificationsKey);

      logWithTimestamp(`👤 [USER-PROFILE-CACHE] All cache cleared for user ${smartuserId}`);
    } catch (error) {
      errorWithTimestamp('👤 [USER-PROFILE-CACHE] Error clearing user cache:', error);
    }
  }

  clearAll(): void {
    this.initialize();
    try {
      const allKeys = storage.getAllKeys();
      let count = 0;

      allKeys.forEach(key => {
        if (
          key.startsWith(CACHE_KEY_PREFIX) ||
          key.startsWith(GAMIFICATION_KEY_PREFIX) ||
          key.startsWith(NOTIFICATIONS_KEY_PREFIX) ||
          key === LAST_REFRESH_KEY
        ) {
          storage.delete(key);
          count++;
        }
      });

      logWithTimestamp(`👤 [USER-PROFILE-CACHE] Cleared ${count} cache entries`);
    } catch (error) {
      errorWithTimestamp('👤 [USER-PROFILE-CACHE] Error clearing all cache:', error);
    }
  }

  getStats(): {
    profileCached: boolean;
    gamificationCached: boolean;
    notificationsCached: boolean;
    lastRefreshAge: number | null;
  } {
    const lastRefresh = this.getLastRefreshTimestamp();
    return {
      profileCached: false,
      gamificationCached: false,
      notificationsCached: false,
      lastRefreshAge: lastRefresh > 0 ? Date.now() - lastRefresh : null,
    };
  }
}

export const userProfileCacheService = new UserProfileCacheService();
